import { useState, useEffect } from 'react'
import { useUser } from './auth'
import './styles/App.css'
import { BrowserRouter as Router, Redirect, Route, Switch, useHistory } from 'react-router-dom'
import PublicRoute from './router/PublicRoute'
import PrivateRoute from './router/PrivateRoute'
import LoginScreen from './screens/auth/LoginScreen'
import SSOCompleteScreen from './screens/auth/SSOCompleteScreen'
import ForgotPasswordScreen from './screens/auth/ForgotPasswordScreen'
import VerifyTokenScreen from './screens/auth/VerifyTokenScreen'
import NewPasswordScreen from './screens/auth/NewPasswordScreen'
import SetPasswordScreen from './screens/auth/SetPasswordScreen'
import SideBar from './components/SideBar'
import Content from './components/content/Content'
import ManagementHome from './components/management/ManagementHome'
import CreateSurvey from './components/content/CreateSurvey'
import Chat from './components/chat/Chat'

import ClassAnalyticsHome from './components/analytics/class/ClassAnalyticsHome'
import StudentAnalyticsHome from './components/analytics/student/StudentAnalyticsHome'
import AnalyticsSearch from './components/analytics/search/AnalyticsSearch'
// import ContentTemplates from './components/content/ContentTemplates';
// import data from './assets/files/apple-app-side-association';
import { ContentContextProvider } from './components/content/ContentContext'
import ContentProgram from './components/content/ContentProgram'
import Dashboard from './components/dashboard/Dashboard'
import InstructionVideos from './components/videos/InstructionVideos'
import ClassroomHome from './components/classroom/ClassroomHome'
import CounselingHome from './components/counseling/CounselingHome'
import StudentWellbeingProfile from './components/counseling/StudentWellbeingProfile'
import ReadyToGo from './components/content/ReadyToGo'

import { AppToaster } from './components/toast/AppToast'
import AnalyticsHome from './components/analytics/AnalyticsHome'
import Settings from './components/settings/Settings'
import * as Sentry from '@sentry/react'
import { getByToken, getCurrentFeatureAnnouncement, logUserTimeAsync, recordFeatureAnnouncementInteraction } from './server/server'
import Loading from './components/Loading'
import RouteErrorBoundary from './components/common/RouteErrorBoundary'
import VersionReloader from './components/common/VersionReloader'
import Fulllogo from './assets/svg/US-full.svg'
import { useSocket } from './context/socket'
import {
    checkIfTeacherHasPrimaryClasses,
    checkIfTeacherHasRegularClasses,
    loadAdditionalFields,
    loadAssignedFeedbacks,
    loadUnseenConversations,
    logLoginTime,
} from './server/usersData'
import CatchUpHome from './components/catchUp/CatchUpHome'
import UploadUserDataScreen from './components/management/UploadUserDataScreen'
import usePageVisibility from './utils/usePageVisibility'
import { useAppContext } from './context/AppContext'
import AnalyzeContent from './components/analytics/AnalyticsContent'
import ManageCustomReports from './components/analytics/CustomReports/ManageCustomReports'
import TermsAndConditionsModal from './components/modals/TermsAndConditionsModal'
import { useTranslation } from 'react-i18next'
import TutorialModal from './components/modals/TutorialModal'
import EarlyDashboard from './components/early/dashboard/EarlyDashboard'
import EarlyClassroomHome from './components/early/classroom/EarlyClassroomHome'
import StudentSelectEmotionScreen from './components/early/classroom/StudentSelectEmotionScreen'
import StudentsSettingsScreen from './components/early/classroom/StudentsSettingsScreen'
import AlertsHome from './components/alerts/AlertsHome'
import NotificationsHome from './components/alerts/NotificationsHome'
import CounsellorAnalyticsHome from './components/analytics/counsellor/CounsellorAnalyticsHome'
import OnboardingScreen from './components/onboarding/OnboardingScreen'
import UploadEngineScreen from './components/management/upload_engine/UploadEngineScreen'
import OrganizationAnalyticsHome from './components/analytics/organization/OrganizationAnalyticsHome'
import PulseCheckScreen from './components/analytics/pulseCheck/PulseCheckScreen'
import NoPermission from './screens/NoPermission'
import { isItAccess } from './constants/Roles'
import { AnalyticsProvider } from './analytics/AnalyticsProvider'

function App() {
    let history = useHistory()
    const { i18n } = useTranslation()
    const { user, setUser, isSignedIn } = useUser()
    const { socketStates, changeSocketState } = useSocket()
    const { appState, setAppState } = useAppContext()
    const [authLoading, setAuthLoading] = useState(true)
    const [pendingAnnouncement, setPendingAnnouncement] = useState(null)
    const [announcementOpen, setAnnouncementOpen] = useState(false)
    const [announcementReady, setAnnouncementReady] = useState(false)
    const pageIsNotVisible = usePageVisibility()
    const [activeTime, setActiveTime] = useState(Date.now())
    const [sidebarAnimation, setSidebarAnimation] = useState(false)
    const searchParams = new URLSearchParams(window.location.search)
    const authToken = searchParams.get('token') ?? null
    const locale = searchParams.get('locale') ?? 'en'
    const [isTCModalVisible, setTCModalVisible] = useState(false)

    const logout = () => {
        setUser({})
        localStorage.clear()
        setTimeout(() => {
            history?.push('/')
        }, 200)
    }

    const checkUserAuth = async () => {
        if (authToken) {
            localStorage.setItem('token', authToken)
            searchParams.delete('token')
            const newUrl = `${window.location.pathname}`
            window.history.replaceState(null, '', newUrl)
            localStorage.setItem('locale', locale)
            i18n.changeLanguage(locale)
        }
        const resp = await getByToken()
        if (resp?.status === 200) {
            const data = resp.data
            const user = data
            if (user.role == 0) {
                logout()
                setAuthLoading(false)
            }
            localStorage.setItem('token', data['token'])
            setUser(user)
            // setAuthLoading(false);
        } else if (resp?.status === 406) {
            setTimeout(() => {
                logout()
                setAuthLoading(false)
            }, 1000)
        } else {
            setTimeout(() => {
                logout()
                setAuthLoading(false)
            }, 1000)
        }
    }

    useEffect(() => {
        if (isSignedIn) {
            initUserData()
            setTimeout(() => setSidebarAnimation(true), 200)
            if (!user.agree && ['en', 'de'].includes(i18n.language)) {
                setTCModalVisible(true)
            }
        } else {
            setSidebarAnimation(false)
            setPendingAnnouncement(null)
            setAnnouncementOpen(false)
            setAnnouncementReady(false)
        }
    }, [isSignedIn])

    useEffect(() => {
        if (isSignedIn && !authLoading && announcementReady && !isTCModalVisible && pendingAnnouncement && !announcementOpen) {
            setAnnouncementOpen(true)
        }
    }, [isSignedIn, authLoading, announcementReady, isTCModalVisible, pendingAnnouncement, announcementOpen])

    const initUserData = async () => {
        setAnnouncementReady(false)
        setPendingAnnouncement(null)
        setAnnouncementOpen(false)
        // IT Access only manages users; skip the chat/wellbeing/tutorial and
        // primary-class boot calls so they don't fire forbidden (403) requests.
        // additionalFields is still needed by the user-management screens.
        if (isItAccess(user)) {
            const additionalFields = await loadAdditionalFields()
            const primaryUi = localStorage.getItem('uiMode') === 'primary'
            setAppState({ additionalFields, primary: primaryUi })
            setAuthLoading(false)
            setAnnouncementReady(true)
            return
        }

        const [unseenConversations, assignedFeedbacks, announcement] = await Promise.all([
            loadUnseenConversations(),
            loadAssignedFeedbacks(),
            loadFeatureAnnouncement(),
        ])
        if (announcement) setPendingAnnouncement(announcement)
        const hasPrimaryClasses = await checkIfTeacherHasPrimaryClasses()

        const additionalFields = await loadAdditionalFields()
        const primaryUi = localStorage.getItem('uiMode') === 'primary'
        setAppState({ additionalFields, primary: primaryUi })
        changeSocketState({ ...socketStates, unseenConversations, assignedFeedbacks })
        setUser({ ...user, hasPrimaryClasses, hasRegularClasses: true, hasOnlyPrimaryStudents: false })
        if (hasPrimaryClasses) {
            const hasRegularClasses = await checkIfTeacherHasRegularClasses()
            setUser({ ...user, hasPrimaryClasses, hasRegularClasses, hasOnlyPrimaryStudents: !hasRegularClasses })
        }

        setAuthLoading(false)
        setAnnouncementReady(true)
    }

    const loadFeatureAnnouncement = async () => {
        const response = await getCurrentFeatureAnnouncement(i18n.language)
        if (response.status === 200) return response.data?.announcement || null
        return null
    }

    const saveAnnouncementInteraction = async (interactionType, lastStep) => {
        if (!pendingAnnouncement?.id) return false
        const announcementId = pendingAnnouncement.id
        if (interactionType === 'dismissed') {
            setPendingAnnouncement(null)
            setAnnouncementOpen(false)
            recordFeatureAnnouncementInteraction({
                announcementId,
                interactionType,
                lastStep,
            }).catch(() => {})
            return true
        }
        const response = await recordFeatureAnnouncementInteraction({
            announcementId,
            interactionType,
            lastStep,
        })
        if (response.status !== 200) return false
        setPendingAnnouncement(null)
        setAnnouncementOpen(false)
        return true
    }

    useEffect(() => {
        // The SSO landing page signs the user in itself. The token check below would find no
        // token yet and log out (clearing localStorage) while the SSO login is completing.
        if (window.location.pathname === '/auth/sso/complete') {
            setAuthLoading(false)
            return
        }
        if (user) {
            checkUserAuth()
        }
        if (window.location.pathname === '/login') {
            setAuthLoading(false)
        }
    }, [])

    useEffect(() => {
        if (isSignedIn) {
            if (!pageIsNotVisible) setActiveTime(Date.now())
            else logUserTime()
        }
    }, [pageIsNotVisible, isSignedIn])

    const logUserTime = () => {
        const data = {
            start_time: activeTime,
            end_time: Date.now(),
            device: 'desktop',
        }
        logUserTimeAsync(data)
        setActiveTime(Date.now())
        uploadLoginLogs()
    }
    const uploadLoginLogs = async () => {
        let restLogins = JSON.parse(localStorage.getItem('loginsLogs') || '[]')
        while (restLogins.length > 0) {
            const data = restLogins.pop()
            const status = await logLoginTime(data, false)
            if (!status) {
                return
            }
        }
        localStorage.setItem('loginsLogs', '[]')
    }

    useEffect(() => {
        uploadLoginLogs()
        if (isSignedIn) window.addEventListener('beforeunload', logUserTime)
        return () => {
            if (isSignedIn) window.removeEventListener('beforeunload', logUserTime)
        }
    }, [isSignedIn])

    const onDisagree = async () => {
        setTCModalVisible(false)
        localStorage.clear()
        setUser({})
    }

    const primaryUI = !!appState?.primary
    const isAdmin = user?.role == 'school_admin' || user?.role_coverage?.target_text == 'All students'
    const accessToOrganization = user.allowedToAccessOrganizationAnalytics
    // const isAdmin = false

    return (
        <>
            <ContentContextProvider>
                <div id="home" className="app">
                    <Router>
                        <AnalyticsProvider enabled={!authLoading}>
                        {isSignedIn && !authLoading && <SideBar />}
                        {authLoading && (
                            <div className="flex justify-center items-center w-full h-screen bg-deepPurple flex-col absolute z-50">
                                <img src={Fulllogo} alt="logo" className="mb-8" />
                                <Loading primaryColor="#FFF" secondaryColor="#FFF" size={75} stroke={4} />
                            </div>
                        )}
                        {!authLoading && (
                            <div
                                className={`home-container ${sidebarAnimation ? 'logged-in' : ''}`}
                                onMouseEnter={(e) => {
                                    e.preventDefault()
                                }}
                                id="home-container"
                            >
                                <RouteErrorBoundary>
                                    <Switch>
                                        <Route exact path="/">
                                            {isSignedIn ? (
                                                isItAccess(user) ? (
                                                    <Redirect to="/management" />
                                                ) : (
                                                    <Redirect to="/dashboard" />
                                                )
                                            ) : (
                                                <Redirect to="/login" />
                                            )}
                                        </Route>
                                        <PublicRoute exact path="/login" component={LoginScreen} />
                                        <PublicRoute exact path="/auth/forgot-password" component={ForgotPasswordScreen} />
                                        <PublicRoute exact path="/auth/verify-token" component={VerifyTokenScreen} />
                                        <PublicRoute exact path="/auth/new-password" component={NewPasswordScreen} />
                                        <PublicRoute exact path="/auth/set-password" component={SetPasswordScreen} />
                                        <PublicRoute exact path="/auth/sso/complete" component={SSOCompleteScreen} />

                                        <PrivateRoute path="/no-permission" component={NoPermission} />

                                        <PrivateRoute path="/dashboard" component={primaryUI ? EarlyDashboard : Dashboard} />
                                        <PrivateRoute exact path="/content" component={Content} />
                                        <PrivateRoute exact path="/readytogo" component={ReadyToGo} />
                                        <PrivateRoute path="/classroom/:pageID(\\?emotion=.*)?" component={ClassroomHome} />
                                        <PrivateRoute path="/chat" component={Chat} />
                                        <PrivateRoute path="/management/upload" component={UploadUserDataScreen} />
                                        <PrivateRoute path="/management/upload_user_data" component={UploadEngineScreen} />
                                        <PrivateRoute path="/management" component={ManagementHome} />
                                        <PrivateRoute path="/catch-up" component={CatchUpHome} />
                                        <PrivateRoute path="/counseling/student_profile/:pageID(\\?id=.*)?" component={StudentWellbeingProfile} />
                                        <PrivateRoute path="/counseling" component={CounselingHome} />
                                        <PrivateRoute path="/settings" component={Settings} />
                                        <PrivateRoute path="/videos" component={InstructionVideos} />
                                        <PrivateRoute exact path="/content/surveys/:pageID(\\?id=.*)?" component={CreateSurvey} />
                                        <PrivateRoute path="/content/program/:pageID(\\?id=.*)?" component={ContentProgram} />

                                        <PrivateRoute path="/analytics/search" component={AnalyticsSearch} />
                                        <PrivateRoute path="/analytics/student" component={StudentAnalyticsHome} />
                                        <PrivateRoute path="/analytics/class" component={ClassAnalyticsHome} />
                                        <PrivateRoute path="/analytics/counsellor" component={CounsellorAnalyticsHome} />
                                        <PrivateRoute
                                            path="/analytics/organization"
                                            component={accessToOrganization ? OrganizationAnalyticsHome : () => <Redirect to="/analytics" />}
                                        />

                                        <PrivateRoute path="/analytics/custom-report" component={ManageCustomReports} />
                                        <PrivateRoute path="/analytics/data" component={AnalyzeContent} />
                                        <PrivateRoute path="/analytics/pulse/:surveyId?" component={PulseCheckScreen} />

                                        <PrivateRoute path="/analytics" component={AnalyticsHome} />

                                        <PrivateRoute path="/early/dashboard" component={EarlyDashboard} />
                                        <PrivateRoute path="/early/classroom/student-select-emotion" component={StudentSelectEmotionScreen} />
                                        <PrivateRoute path="/early/classroom/student-settings" component={StudentsSettingsScreen} />
                                        <PrivateRoute path="/early/classroom" component={EarlyClassroomHome} />

                                        <PrivateRoute path="/alerts/notification" component={isAdmin ? NotificationsHome : () => <Redirect to="/alerts" />} />
                                        <PrivateRoute path="/alerts" component={AlertsHome} />
                                        {/* Inactivity now lives as a Management tab (settings is a modal there);
                                            keep the old links working. */}
                                        <PrivateRoute path="/inactivity" component={() => <Redirect to="/management?tab=inactivity" />} />
                                        <PrivateRoute path="/onboarding" component={OnboardingScreen} />

                                        <Route path="*">
                                            {isSignedIn ? (
                                                isItAccess(user) ? (
                                                    <Redirect to="/management" />
                                                ) : (
                                                    <Redirect to="/dashboard" />
                                                )
                                            ) : (
                                                <Redirect to="/" />
                                            )}
                                        </Route>
                                    </Switch>
                                </RouteErrorBoundary>
                            </div>
                        )}
                        {isSignedIn && (
                            <TutorialModal
                                modalIsOpen={announcementOpen}
                                announcement={pendingAnnouncement}
                                onInteraction={saveAnnouncementInteraction}
                            />
                        )}
                        <TermsAndConditionsModal closeModal={() => setTCModalVisible(false)} modalIsOpen={isTCModalVisible} onDisagree={onDisagree} />
                        <VersionReloader />
                        </AnalyticsProvider>
                    </Router>
                </div>
                <AppToaster />
            </ContentContextProvider>
        </>
    )
}

export default Sentry.withProfiler(App)
// export default App;
